<script lang="ts">
import { useModal } from '@monogrid/vue-boost/integrations/focus-trap'
import { templateRef, useVModel } from '@vueuse/core'
import type { InjectionKey } from 'vue'
import { computed, defineComponent, inject, provide } from 'vue'

import { loaderInjectionKey } from '@/App.vue'

type ModalWrapperPublicInstance = {
  dismiss?: () => void
}

export const modalInjectionKey: InjectionKey<ModalWrapperPublicInstance> = Symbol('overlay')

export default defineComponent({
  name: 'ModalWrapper',
  inheritAttrs: false,
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ['dismiss', 'update:visible'],
  setup (props, { emit }) {
    const loader = inject(loaderInjectionKey)
    const wrapper = templateRef<HTMLElement>('wrapper')
    const visible = useVModel(props, 'visible', emit)
    const active = computed(() => !loader.value?.visible && visible.value)

    const dismiss = () => {
      if (!visible.value) return
      visible.value = false
      emit('dismiss')
    }

    useModal(wrapper, dismiss)

    provide(modalInjectionKey, { dismiss })

    return {
      active,
      dismiss
    }
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition>
      <div
        v-if="active"
        v-bind="$attrs"
        ref="wrapper"
        class="modal flex fixed inset-0 items-center justify-center cursor-pointer"
        role="dialog"
        aria-modal="true"
        @click.self="dismiss"
      >
        <slot :dismiss="dismiss" />
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.modal {
  z-index: $z-modal;
  background-color: color('black');

  &::v-deep(> *:not(button, a)) {
    cursor: auto;
  }
}
</style>
